// «الصيانة» — requests / work orders with filters (GET /maintenance/items). The open backlog
// is listed whole; any other status reads the page's date range by opening date.
import { useCallback, useEffect, useRef, useState } from "react";
import { AlarmClock, ChevronLeft, ChevronRight, Repeat } from "lucide-react";
import api from "@/lib/api";
import { apiErrorText } from "@/lib/errorText";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MAINT } from "@/constants/testIds";
import {
    bucketName, critLabel, critTone, fmtDuration, fmtTemplate, fmtWhen, itemBranchName, statusLabel, statusTone,
} from "./maintText";

const ALL = "all";
const WO_STATUSES = ["open", "awaiting_close", "awaiting_branch", "assigned", "in_progress", "reopened", "completed", "closed", ALL];
// the maintenance system's own request statuses: new | accepted | rejected | closed (open = new | accepted)
const WR_STATUSES = ["open", "new", "accepted", "rejected", "closed", ALL];
const CRITS = ["critical", "emergency", "urgent", "high", "medium", "normal", "low", "unknown"];
// backlog states are "as of now", like their KPI cards: listed whole, never by date
const BACKLOG = ["open", "awaiting_close", "awaiting_branch"];

const statusOptLabel = (t, s) => (s === "open" ? t("mt_status_open") : s === ALL ? t("mt_status_all")
    : s === "awaiting_close" ? t("mt_status_awaiting_close") : s === "awaiting_branch" ? t("mt_status_awaiting_branch")
        : statusLabel(t, s));

function FilterSelect({ id, label, value, onChange, options, dir }) {
    return (
        <label className="min-w-0 space-y-1">
            <span className="block text-[11px] font-bold text-[color:var(--ink-muted)]">{label}</span>
            <Select value={value} onValueChange={onChange} dir={dir}>
                <SelectTrigger data-testid={MAINT.filter(id)} className="h-10 w-full text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                    {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
            </Select>
        </label>
    );
}

function ItemRow({ it, t, lang, onOpen }) {
    return (
        <button type="button" data-testid={MAINT.listRow(it.type, it.id)} onClick={() => onOpen(it)}
                data-overdue={it.overdue ? "true" : "false"}
                className={`w-full space-y-1.5 border-b border-[color:var(--separator)] px-3 py-2.5 text-start transition-[background-color] duration-150 last:border-b-0 hover:bg-[color:var(--brand-orange-soft)] ${it.overdue ? "bg-[color:var(--danger-soft)]/40" : ""}`}>
            <div className="flex items-start justify-between gap-2">
                <span className="min-w-0">
                    <span className="block truncate text-sm font-bold text-[color:var(--ink)]"><bdi>{it.title || it.code || "—"}</bdi></span>
                    <span className="block truncate text-[11px] text-[color:var(--ink-muted)]">
                        <span dir="ltr">{it.code || ""}</span>
                        <span> · </span>
                        <span>{itemBranchName(it, lang)}</span>
                        {it.type === "wo" && it.assigned_to_name ? <span> · <bdi>{it.assigned_to_name}</bdi></span> : null}
                    </span>
                </span>
                <span className="shrink-0 text-[10px] text-[color:var(--ink-faint)]">{fmtWhen(it.created_at, lang)}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant={statusTone(it.status)} className="px-2 py-0 text-[10px]"><span>{statusLabel(t, it.status)}</span></Badge>
                <Badge variant={critTone(it.criticality)} className="px-2 py-0 text-[10px]"><span>{critLabel(t, it.criticality)}</span></Badge>
                {it.overdue ? (
                    <Badge variant="destructive" className="px-2 py-0 text-[10px]">
                        <AlarmClock className="h-3 w-3" />
                        <span>{fmtTemplate(t("mt_overdue_by"), { d: fmtDuration(it.overdue_minutes, t) })}</span>
                    </Badge>
                ) : null}
                {it.repeat_of ? (
                    <Badge variant="brown" className="px-2 py-0 text-[10px]"><Repeat className="h-3 w-3" /><span>{t("mt_kpi_repeat")}</span></Badge>
                ) : null}
                {it.mapping_state === "suggested" ? (
                    <Badge variant="warning" className="px-2 py-0 text-[10px]"><span>{t("mt_suggested")}</span></Badge>
                ) : null}
            </div>
        </button>
    );
}

/** `query` = {type, status, branch, criticality, overdue}; the page owns it so a KPI card or a
 *  branch row can set it. `range` = {from, to} of the page. */
export function ItemsList({ query, onQuery, range, rows, t, lang, dir, onOpen, reloadKey }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [page, setPage] = useState(1);
    const seq = useRef(0);
    const q = query;
    const usesRange = !BACKLOG.includes(q.status) && !q.overdue;

    useEffect(() => { setPage(1); }, [q.type, q.status, q.branch, q.criticality, q.overdue, range.from, range.to]);

    const load = useCallback(async () => {
        const params = { type: q.type, page };
        if (q.status && q.status !== ALL) params.status = q.status;
        if (q.branch && q.branch !== ALL) params.branch = q.branch;
        if (q.criticality && q.criticality !== ALL) params.criticality = q.criticality;
        if (q.overdue) params.overdue = true;
        if (usesRange) { params.from = range.from; params.to = range.to; }
        const mine = ++seq.current;          // a slower, older answer never replaces a newer one
        try {
            const r = await api.get("/maintenance/items", { params });
            if (mine !== seq.current) return;
            setData(r.data);
            setErr(null);
        } catch (e) {
            if (mine === seq.current) setErr(apiErrorText(e));
        }
    }, [q.type, q.status, q.branch, q.criticality, q.overdue, page, usesRange, range.from, range.to]);

    useEffect(() => { load(); }, [load, reloadKey]);

    const set = (k) => (v) => onQuery({ ...q, [k]: v, ...(k === "type" ? { status: "open", overdue: false } : {}) });
    const statuses = q.type === "wo" ? WO_STATUSES : WR_STATUSES;
    const branchOpts = [[ALL, t("mt_all_branches")], ...(rows || []).map((r) => [r.bucket, bucketName(r, lang)])];
    const critOpts = [[ALL, t("mt_all")], ...CRITS.map((c) => [c, critLabel(t, c)])];
    const Prev = dir === "rtl" ? ChevronRight : ChevronLeft;
    const Next = dir === "rtl" ? ChevronLeft : ChevronRight;

    return (
        <section data-testid={MAINT.lists} className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-base font-extrabold text-[color:var(--ink)]">{t("mt_lists_title")}</h3>
                <div className="inline-flex rounded-full border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1">
                    {["wo", "wr"].map((k) => (
                        <button key={k} type="button" data-testid={MAINT.listTab(k)} data-active={q.type === k ? "true" : "false"}
                                onClick={() => q.type !== k && set("type")(k)}
                                className={`rounded-full px-3 py-1.5 text-xs font-bold transition-[background-color,color] duration-150 ${q.type === k
                                    ? "bg-[color:var(--brand-orange)] text-white" : "text-[color:var(--ink-muted)] hover:bg-[color:var(--brand-orange-soft)]"}`}>
                            {t(`mt_tab_${k}`)}
                        </button>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-2 gap-2 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3 sm:grid-cols-4">
                <FilterSelect id="status" label={t("mt_f_status")} value={q.status || ALL} onChange={set("status")} dir={dir}
                              options={statuses.map((s) => [s, statusOptLabel(t, s)])} />
                <FilterSelect id="branch" label={t("mt_f_branch")} value={q.branch || ALL} onChange={set("branch")} dir={dir} options={branchOpts} />
                <FilterSelect id="criticality" label={t("mt_f_criticality")} value={q.criticality || ALL} onChange={set("criticality")} dir={dir} options={critOpts} />
                {q.type === "wo" ? (
                    <label className="flex items-end gap-2 pb-2 text-xs font-semibold text-[color:var(--ink)]">
                        <Switch data-testid={MAINT.overdueToggle} checked={!!q.overdue} onCheckedChange={set("overdue")} />
                        <span>{t("mt_f_overdue")}</span>
                    </label>
                ) : null}
                <p className="col-span-2 text-[11px] text-[color:var(--ink-faint)] sm:col-span-4">
                    {usesRange ? t("mt_range_applies") : t("mt_open_note")}
                </p>
            </div>
            <Card className="overflow-hidden rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
                {err ? (
                    <p role="alert" className="px-4 py-6 text-center text-sm text-[color:var(--danger)]">{err}</p>
                ) : !data ? (
                    <div className="space-y-2 p-3"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>
                ) : !data.items.length ? (
                    <p data-testid={MAINT.listEmpty} className="px-4 py-8 text-center text-sm text-[color:var(--ink-muted)]">{t("mt_list_empty")}</p>
                ) : (
                    data.items.map((it) => <ItemRow key={`${it.type}-${it.id}`} it={it} t={t} lang={lang} onOpen={onOpen} />)
                )}
            </Card>
            {data && data.pages > 1 ? (
                <div className="flex items-center justify-between gap-2 text-xs text-[color:var(--ink-muted)]">
                    <button type="button" data-testid={MAINT.pagePrev} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                            className="inline-flex h-9 items-center gap-1 rounded-full border border-[color:var(--border-soft)] px-3 font-bold disabled:opacity-40">
                        <Prev className="h-4 w-4" /><span>{t("mt_prev")}</span>
                    </button>
                    <span className="tnum">{fmtTemplate(t("mt_page_of"), { p: data.page, n: data.pages, total: data.total })}</span>
                    <button type="button" data-testid={MAINT.pageNext} disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}
                            className="inline-flex h-9 items-center gap-1 rounded-full border border-[color:var(--border-soft)] px-3 font-bold disabled:opacity-40">
                        <span>{t("mt_next")}</span><Next className="h-4 w-4" />
                    </button>
                </div>
            ) : null}
        </section>
    );
}
